import { ReactNode } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { pxToRem } from '@utils/converters';
import { transitionFast } from '@utils/transitions';

import { useFormBuilderContext } from '../context';
import { DropLine } from '../drop-line';
import { StateField } from '../state';

export const FieldDropzone = ({ field, children }: { field: StateField; children: ReactNode }) => {
  const {
    store: { useIsNewAndUntouched },
  } = useFormBuilderContext();
  const isNew = useIsNewAndUntouched();
  const { setNodeRef, isOver, active } = useDroppable({
    id: `${field.id}-top`,
    data: {
      field,
      isTopHalf: true,
    },
  });

  if (field.kind === 'submit' && isNew) {
    // We show a helper box whenever the form is new and
    // we allow dragging over that box to insert the first
    // element. If we also allowed dragging over the submit
    // button then it would look weird.
    return <>{children}</>;
  }

  if (active?.data.current?.field?.id === field.id && field.builder.parentId) {
    return <>{children}</>;
  }

  return (
    <>
      {isOver && <DropLine />}
      <Wrapper data-drag-active={active !== null}>
        <DropContainer data-drag-over={isOver}>
          {active && <DropArea $top ref={setNodeRef} />}
          {children}
        </DropContainer>
      </Wrapper>
    </>
  );
};

const DropArea = styled.div<{ $top?: boolean }>((p) => ({
  position: 'absolute',
  top: pxToRem(-8),
  left: pxToRem(-8),
  bottom: pxToRem(-8),
  right: pxToRem(-8),
}));

const DropContainer = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { type: 'just' } },
})({
  position: 'relative',
  transition: transitionFast('padding'),
  '[data-drag-over="true"]': {
    paddingTop: 4,
  },
});

const Wrapper = styled.div({
  position: 'relative',
  '[data-drag-active="true"]': {
    pointerEvents: 'none',
  },
});
